import { cn } from '../../lib/cn';
import { ORG_BRANCHES, ORG_CEO, ORG_CHART_CAPTION, ORG_CHART_TITLE, type OrgPerson, SUPPORT_FUNCTIONS, VACANT } from './content/divisions';

const Box = ({ person, strong = false }: { person: OrgPerson; strong?: boolean }) => (
  <div
    className={cn(
      'rounded-sm border-[1.5px] bg-card px-2 py-1.5 text-center',
      person.outsourced ? 'border-dashed border-primary' : 'border-foreground/80',
      strong && 'bg-primary text-primary-foreground',
    )}
    data-org-role={person.role}
  >
    <p
      className={cn(
        'text-[9px] uppercase leading-tight tracking-wide',
        strong ? 'opacity-85' : person.outsourced ? 'text-primary' : 'text-muted-foreground',
      )}
    >
      {person.role}
    </p>
    <p className={cn('text-xs font-medium leading-snug', person.name === VACANT && 'italic text-muted-foreground')}>{person.name}</p>
  </div>
);

const Branch = ({ person }: { person: OrgPerson }) => (
  <div className="flex min-w-0 flex-col items-stretch gap-1.5">
    <Box person={person} />
    {person.reports && person.reports.length > 0 ? (
      <div className="ms-2 space-y-1.5 border-s border-border ps-2">
        {person.reports.map((report, index) => (
          <Branch
            key={`${report.role}-${index}`}
            person={report}
          />
        ))}
      </div>
    ) : null}
  </div>
);

/** The board's team plan (static, `content/divisions.ts`): CEO, six branches, in-house vs outsourced, support functions. */
export const OrgChart = () => (
  <section
    className="space-y-4 rounded-lg border border-border bg-card p-5"
    data-org-chart
  >
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{ORG_CHART_CAPTION}</p>
        <h2 className="text-lg font-semibold text-foreground">{ORG_CHART_TITLE}</h2>
      </div>
      <div className="flex gap-4 text-[11px] uppercase tracking-wide text-muted-foreground">
        <span className="flex items-center gap-2">
          <span className="inline-block w-5 border-t-2 border-foreground/80" />
          In-house resource
        </span>
        <span className="flex items-center gap-2 text-primary">
          <span className="inline-block w-5 border-t-2 border-dashed border-primary" />
          Outsourced resource
        </span>
      </div>
    </div>
    <div className="mx-auto w-56">
      <Box
        person={ORG_CEO}
        strong
      />
    </div>
    <div className="grid grid-cols-2 gap-3 border-t border-border pt-4 md:grid-cols-3 xl:grid-cols-6">
      {ORG_BRANCHES.map((branch) => (
        <Branch
          key={branch.role}
          person={branch}
        />
      ))}
    </div>
    <div className="border-t border-border pt-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Support functions</p>
      <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-foreground">
        {SUPPORT_FUNCTIONS.map((each) => (
          <li key={each}>– {each}</li>
        ))}
      </ul>
    </div>
  </section>
);
